import React, { useState } from "react";
import { Button, Menu, MenuItem, Stack } from "@mui/material";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { Link } from "react-router-dom";

export interface RowAction {
  label: string;
  onClick?: () => void;
  href?: string;
  tone?: "default" | "error" | "primary" | "warning";
  loading?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  startIcon?: React.ReactNode;
}

interface RowActionsProps {
  actions: RowAction[];
  inline?: number;
}

/**
 * Standard text-button row actions inspired by HRIS-MVP design system.
 * Up to `inline` actions render directly as clean text buttons; the rest collapse into a "More" menu.
 */
export function RowActions({ actions, inline = 2 }: RowActionsProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const visible = actions.filter((action) => !action.hidden);
  const direct = visible.length <= inline + 1 ? visible : visible.slice(0, inline);
  const overflow = visible.length <= inline + 1 ? [] : visible.slice(inline);

  const getButtonColor = (tone?: string) => {
    if (tone === "error") return "error";
    if (tone === "warning") return "warning";
    return "primary";
  };

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ width: "100%" }}
      onClick={(e) => e.stopPropagation()} // Prevent triggering row click when clicking action buttons
    >
      {direct.map((action) => (
        <Button
          key={action.label}
          size="small"
          variant="text"
          color={getButtonColor(action.tone)}
          disabled={action.disabled || action.loading}
          startIcon={action.startIcon}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick?.();
          }}
          sx={{
            textTransform: "none",
            fontWeight: 650,
            fontSize: "12.5px",
            minWidth: "auto",
            px: 1.25,
            py: 0.4,
            borderRadius: "4px",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor:
                action.tone === "error"
                  ? "rgba(220, 38, 38, 0.08)"
                  : "rgba(0, 114, 188, 0.08)",
            },
          }}
          {...(action.href ? { component: Link, to: action.href } : {})}
        >
          {action.loading ? "Loading..." : action.label}
        </Button>
      ))}

      {overflow.length > 0 && (
        <>
          <Button
            size="small"
            variant="text"
            color="primary"
            endIcon={<KeyboardArrowDownRounded fontSize="small" />}
            onClick={(event) => {
              event.stopPropagation();
              setAnchor(event.currentTarget);
            }}
            aria-haspopup="menu"
            sx={{
              textTransform: "none",
              fontWeight: 650,
              fontSize: "12.5px",
              minWidth: "auto",
              px: 1,
              py: 0.4,
              borderRadius: "4px",
              color: "#64748B",
            }}
          >
            More
          </Button>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={(e: any) => {
              if (e?.stopPropagation) e.stopPropagation();
              setAnchor(null);
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                borderRadius: "6px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                minWidth: 140,
                py: 0.5,
              },
            }}
          >
            {overflow.map((action) => (
              <MenuItem
                key={action.label}
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchor(null);
                  action.onClick?.();
                }}
                sx={{
                  color: action.tone === "error" ? "error.main" : "#1E293B",
                  fontSize: 13,
                  fontWeight: 500,
                  py: 1,
                }}
              >
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
}
